/**
 * Tiny tile factory for crafted lesson deals. Each builder hands out tiles with
 * globally unique, sequential ids so the engine (which tracks copies by id) can
 * never confuse two physical tiles in the same deal. Every lesson script makes
 * its own builder, so ids only need to be unique within one script.
 *
 * `fillerBot` produces a benign 13-tile hand — one lone honour plus four runs —
 * whose *worst* tile is always that lone honour (usefulness −1, the global
 * minimum). That makes a pass-through bot discard its honour deterministically,
 * regardless of `EasyStrategy`'s seeded noise, so lessons can rely on the honour
 * leaving play and the turn passing cleanly to the next seat.
 */

import type { Honor, Rank, Suit, Tile } from '../../game/types.js';

/** Highest rank a run can start on and still fit three ascending tiles. */
const MAX_RUN_START: Rank = 7;

export interface DealBuilder {
  suit(suit: Suit, rank: Rank): Tile;
  honor(honor: Honor): Tile;
  /** Three ascending same-suit tiles starting at `start` (≤ 7). */
  run(suit: Suit, start: Rank): Tile[];
}

export function createDealBuilder(): DealBuilder {
  let id = 0;
  const suit = (s: Suit, rank: Rank): Tile => ({ kind: 'suit', suit: s, rank, id: id++ });
  const honor = (h: Honor): Tile => ({ kind: 'honor', honor: h, id: id++ });
  const run = (s: Suit, start: Rank): Tile[] => {
    if (start > MAX_RUN_START) throw new Error(`run start ${start} leaves no room for three tiles`);
    return [suit(s, start), suit(s, (start + 1) as Rank), suit(s, (start + 2) as Rank)];
  };
  return { suit, honor, run };
}

/** A pass-through bot: one lone honour (its guaranteed discard) plus four runs. */
export function fillerBot(
  b: DealBuilder,
  loneHonor: Honor,
  runs: readonly (readonly [Suit, Rank])[],
): Tile[] {
  const tiles: Tile[] = [b.honor(loneHonor)];
  for (const [s, start] of runs) tiles.push(...b.run(s, start));
  return tiles;
}
